import RefreshIcon from "@mui/icons-material/Refresh";
import { Button, Grid, Link, Stack, Typography } from "@mui/material"
import { crudGetList, RecordContextProvider, ReduxState } from "ra-core"
import { FC, useEffect, useRef, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { Helmet } from 'react-helmet-async'
import FieldsMappingList from "@/components/settings/encryption/FieldsMappingList"
import { TableSelectionCard } from "@/components/settings/encryption/TableSelectionCard"
import { RecPatch } from "@/dataProvider/tools"
import { OdResourcePageProps } from "@/types/resource"
import { pages, sorts } from "@/utils/paramsWithGetMainList"
import { useMutateEncryptionMappingsController } from "./mutateEncryptionMappingsController"
import { MutateEncryptionMappingsContextProvider } from "./mutateEncryptionMappingsContext"
import { useResourceRegister } from "@/utils/resourceRegister"
import { decodeHashid, genUrlWithHashid } from "@/utils/hashid"
import { Content } from "@/components/content/Content"
import { Route, Switch } from "react-router-dom"
import EditNoSqlEncryptionMappings, { useSchemaReadyDatabase } from "./EditNoSqlEncryptionMappings"
import { DatabaseResponseDto } from "@/gen/api/encrypt"
import { crudGetOneEx } from "@/actions/crudFetchEx"
import { useEventBus } from "@/contexts/EventBusContext/useEventBus";


const EditEncryptionMappings: FC<OdResourcePageProps & RecPatch> = (props) => {
  useResourceRegister({ name: 'db-fields-mapping-count' })
  useResourceRegister({ name: 'tables' })
  useResourceRegister({ name: 'collections' })
  useResourceRegister({ name: 'fields' })
  useResourceRegister({ name: 'collection-properties' })
  useResourceRegister({ name: 'mapping-rules' })
  const { id: hId, match, ...rest } = props
  const basePath = match ? match.path : '';
  const id = decodeHashid(hId || '')
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(
      crudGetList('encryption/mappings', pages(), sorts(), {})
    )
  }, [dispatch])

  return (
    <Switch>
      <Route path={`${basePath}/ns`}>
        <EditNoSqlEncryptionMappings id={id} {...rest} />
      </Route>
      <Route path={`${basePath}`}>
        <EditEncryptionMappingsBody id={id} {...rest} />
      </Route>
    </Switch>
  )
}


interface EditEncryptionMappingsBodyProps extends OdResourcePageProps, RecPatch { }

export const EditEncryptionMappingsBody: FC<EditEncryptionMappingsBodyProps> = (({
  id,
  options,
  resource = 'encryption/mappings',
}) => {
  const { encryptMappingCtrl } = useMutateEncryptionMappingsController()
  const { currTable } = encryptMappingCtrl
  const databaseEm = useSchemaReadyDatabase(id)

  const showReviewReminder = useShowReviewRemainder(id)
  
  const refreshForReview = () => {
    if (!databaseEm || !currTable) { return }
    window.location.assign(window.location.href)
  }
  useEventBus('fields-mapping-list')


  return (
    <MutateEncryptionMappingsContextProvider value={encryptMappingCtrl}>
      <Content 
        pageName="Edit Encryption Mappings" 
        resourceName="Edit Encryption Mappings" 
        breadcrumbs={[ ...(options?.breadcrumbs ?? []), { label: 'Encryption Mappings', path: `/${resource}` } ]}
        mainActions={
          databaseEm &&
          <Link href={genUrlWithHashid(`/encryption/obfs/mappings/$0/edit`, databaseEm.databaseId!, databaseEm.databaseName)} target="_blank" variant="caption"
            sx={{ mb: 1, mr: 1, display: 'inline-block' }}
          >
            Obfuscation Mappings...
          </Link>
        }
      >
        <Helmet>
          <title>Edit Encryption Mappings | OnData</title>
        </Helmet>

        <RecordContextProvider value={databaseEm}>
          <Grid container spacing={4}>
            <Grid item xs={12}>
                <TableSelectionCard useSource="tables" />
            </Grid>
            {showReviewReminder &&
              <Grid item xs={12}>
                <Stack direction="row" alignItems="center" >
                  <Typography variant="body2">
                    The sensitive data columns in this database are automatically identified and classified, with appropriate encryption rules assigned. <br/>
                    Please review all tables and adjust as needed.</Typography>
                  <Button onClick={refreshForReview} sx={{ ml: 2 }} size="small" startIcon={<RefreshIcon />}>Reload Page</Button>
                </Stack>
              </Grid>}
            <Grid item xs={12}>
              <FieldsMappingList />
            </Grid>
          </Grid>
        </RecordContextProvider>
      </Content>
    </MutateEncryptionMappingsContextProvider>
  )
})

export default EditEncryptionMappings;


const useShowReviewRemainder = (id: any) => {
  const [states, setStates] = useState({ showReviewReminder: false })
  const autoClassified = useSelector<ReduxState, boolean | undefined>(store => {
    const eDatabase: DatabaseResponseDto | undefined = store.admin.resources['db-esc-status'].data[id] as any
    return eDatabase?.autoClassified
  })
  const dispatch = useDispatch()
  const { current: refBag } = useRef({ timer: null as number | null })
  useEffect(() => {
    if (autoClassified == null) {
      dispatch(crudGetOneEx('db-esc-status', id, ''))
    } else if (!autoClassified) {
      refBag.timer = window.setInterval(() => {
        dispatch(crudGetOneEx('db-esc-status', id, ''))
      }, 15000)
      return () => { 
        if (refBag.timer) {
          window.clearInterval(refBag.timer) 
        }
      }
    } else {
      if (refBag.timer) {
        window.clearInterval(refBag.timer)
        refBag.timer = null
        setStates(v => ({ ...v, showReviewReminder: true }))
      }
    }
  }, [autoClassified, dispatch, id, refBag])

  return states.showReviewReminder
}

